import { useEffect, useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { Map as MapIcon, Image as ImageIcon } from "lucide-react";

/**
 * Compact preview that renders the plan image (or PDF first page) with markup overlay.
 * Use for embedding in lot detail / client review.
 *
 * Props:
 *   planId, markups (array), token (optional, for public access), small (boolean)
 */
export default function MapPreview({ planId, markups, token, small }) {
  const [src, setSrc] = useState(null);
  const [isPdf, setIsPdf] = useState(false);

  const url = useMemo(() => {
    if (!planId) return null;
    return token ? `${API}/plans/${planId}/file?token=${token}` : `${API}/plans/${planId}/file`;
  }, [planId, token]);

  useEffect(() => {
    if (!url) return;
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(url, { credentials: token ? "omit" : "include" });
        const ctype = res.headers.get("content-type") || "";
        const buf = await res.arrayBuffer();
        if (cancel) return;
        if (ctype.includes("pdf")) {
          setIsPdf(true);
          try {
            const pdfjs = await import("pdfjs-dist/build/pdf.mjs");
            try {
              pdfjs.GlobalWorkerOptions.workerSrc = (await import("pdfjs-dist/build/pdf.worker.mjs?url")).default;
            } catch {
              pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
            }
            const doc = await pdfjs.getDocument({ data: buf }).promise;
            const page = await doc.getPage(1);
            const viewport = page.getViewport({ scale: 1.2 });
            const canvas = document.createElement("canvas");
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
            if (!cancel) setSrc(canvas.toDataURL("image/png"));
          } catch (e) { console.warn("PDF preview failed", e); }
        } else {
          setIsPdf(false);
          const blob = new Blob([buf], { type: ctype });
          if (!cancel) setSrc(URL.createObjectURL(blob));
        }
      } catch (e) {
        console.warn("Preview fetch failed", e);
      }
    })();
    return () => { cancel = true; };
  }, [url, token]);

  if (!planId) {
    return (
      <div className={`grid place-items-center rounded-md border border-dashed border-slate-300 bg-slate-50 ${small ? "h-32" : "h-60"} text-slate-400`}>
        <MapIcon className="size-8" />
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden rounded-md border border-slate-200 bg-slate-100 ${small ? "h-32" : "h-72"}`} data-testid="map-preview">
      {src ? (
        <img src={src} alt="Plan" className="size-full object-contain" />
      ) : (
        <div className="grid h-full place-items-center text-slate-400"><ImageIcon className="size-8" /></div>
      )}
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="pointer-events-none absolute inset-0 size-full">
        {(markups || []).map(m => <Shape key={m.id} m={m} />)}
      </svg>
    </div>
  );
}

function Shape({ m }) {
  const c = m.colour || "#0369a1";
  const pts = (m.coordinates || []).map(p => `${p.x * 100},${p.y * 100}`).join(" ");
  switch (m.markup_type) {
    case "pin":
      return <circle cx={m.coordinates[0]?.x * 100} cy={m.coordinates[0]?.y * 100} r="1.4" fill={c} stroke="white" strokeWidth="0.3" />;
    case "rectangle":
    case "highlight": {
      const [a, b] = m.coordinates || [];
      if (!a || !b) return null;
      const x = Math.min(a.x, b.x) * 100, y = Math.min(a.y, b.y) * 100;
      const w = Math.abs(a.x - b.x) * 100, h = Math.abs(a.y - b.y) * 100;
      return <rect x={x} y={y} width={w} height={h}
                   fill={m.markup_type === "highlight" ? c : "none"}
                   fillOpacity={m.markup_type === "highlight" ? 0.25 : 0}
                   stroke={c} strokeWidth="0.4" />;
    }
    case "polygon":
      return <polygon points={pts} fill={c} fillOpacity="0.22" stroke={c} strokeWidth="0.4" />;
    case "line":
    case "arrow":
    case "measurement": {
      const [a, b] = m.coordinates || [];
      if (!a || !b) return null;
      return <line x1={a.x * 100} y1={a.y * 100} x2={b.x * 100} y2={b.y * 100} stroke={c} strokeWidth="0.4" />;
    }
    default:
      return null;
  }
}
